import { AuthorFocusSchema, FreeChoiceSchema, SpecificBookSchema } from "@book-club/shared";
import { z } from "zod";

/**
 * Helper to make optional URL fields also accept empty strings (for form inputs)
 */
const optionalUrlOrEmpty = z.string().url().optional().or(z.literal(""));

/**
 * Helper to make optional string fields also accept empty strings (for form inputs)
 */
const optionalStringOrEmpty = z.string().optional().or(z.literal(""));

/**
 * Form schema for specific book type
 * Derived from SpecificBookSchema, omitting id and createdAt
 * Used in AddBookPage and EditBookPage
 * All fields are optional during editing, but title and author are required for submission
 */
export const SpecificBookFormSchema = SpecificBookSchema.omit({
    archivedAt: true,
    createdAt: true,
    id: true,
}).extend({
    /** Amazon URL for referral link (allows empty string for form inputs) */
    amazonUrl: optionalUrlOrEmpty,
    /** Book author (optional during editing, required for submission) */
    author: z.string().optional().or(z.literal("")),
    /** URL to cover image (allows empty string for form inputs) */
    coverUrl: optionalUrlOrEmpty,
    /** ISBN (allows empty string for form inputs) */
    isbn: optionalStringOrEmpty,
    /** Book title (optional during editing, required for submission) */
    title: optionalStringOrEmpty,
});

/**
 * TypeScript type for specific book form data
 */
export type SpecificBookFormData = z.infer<typeof SpecificBookFormSchema>;

/**
 * Form schema for author focus book type
 * Derived from AuthorFocusSchema, omitting id and createdAt
 * Used in AddBookPage and EditBookPage
 * Author is optional during editing, but required for submission
 */
export const AuthorFocusFormSchema = AuthorFocusSchema.omit({ archivedAt: true, createdAt: true, id: true }).extend({
    /** Amazon URL for referral link (allows empty string for form inputs) */
    amazonUrl: optionalUrlOrEmpty,
    /** Book author (optional during editing, required for submission) */
    author: optionalStringOrEmpty,
});

/**
 * TypeScript type for author focus form data
 */
export type AuthorFocusFormData = z.infer<typeof AuthorFocusFormSchema>;

/**
 * Form schema for free choice book type
 * Derived from FreeChoiceSchema, omitting id and createdAt
 * Used in AddBookPage and EditBookPage
 * No fields required
 */
const FreeChoiceFormSchema = FreeChoiceSchema.omit({ archivedAt: true, createdAt: true, id: true });

/**
 * Book form schema - discriminated union of all book types
 * Used for form validation in AddBookPage and EditBookPage
 */
const bookFormSchemaUnion = z.discriminatedUnion("type", [
    SpecificBookFormSchema,
    AuthorFocusFormSchema,
    FreeChoiceFormSchema,
]);

/**
 * Book form schema that allows type to be undefined (for initial form state)
 */
export const bookFormSchema = bookFormSchemaUnion.or(
    z.object({
        type: z.undefined().optional(),
    })
);

/**
 * TypeScript type for book form data
 * Allows partial fields during editing and undefined type for initial state
 */
export type BookFormData = z.infer<typeof bookFormSchema>;
